import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuthContext } from './AuthProvider';
import settings from './settings';

const { REACT_APP_BACKEND_URL } = settings;

interface AgentOption {
    description: string;
    id: string;
    name: string; // Store agent's name
}

interface AgentContextType {
    agents: AgentOption[];
    selectedAgent: string;
    selectedAgentName: string | undefined; // Include selected agent name
    setSelectedAgent: (value: string) => void;
    loading: boolean; 
}

const AgentContext = createContext<AgentContextType | undefined>(undefined);

export const useAgent = (): AgentContextType => {
    const context = useContext(AgentContext);
    if (!context) {
        throw new Error('useAgent must be used within an AgentProvider');
    }
    return context;
};

interface AgentProviderProps {
    children: ReactNode;
}

export const AgentProvider: React.FC<AgentProviderProps> = ({ children }) => {
    const [agents, setAgents] = useState<AgentOption[]>([]);
    const [selectedAgent, setSelectedAgent] = useState<string>('');
    const [selectedAgentName, setSelectedAgentName] = useState<string | undefined>(undefined); // Track agent's name
    const [loading, setLoading] = useState<boolean>(true); 
    const { loginWithMicrosoft } = useAuthContext();

    useEffect(() => {
        const fetchAgents = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem('access_token');
        
                if (!token) {
                    console.warn('No access token found, redirecting to login.');
                    loginWithMicrosoft();  // Trigger login if no token is found
                    return;
                }
        
                const response = await fetch(`${REACT_APP_BACKEND_URL}/agents/`, {
                    method: "GET",
                    credentials: "include",
                    headers: {
                        "Authorization": `Bearer ${token}`,  // Include the token in the Authorization header
                    },
                });
    
                if (response.status === 401) {
                    loginWithMicrosoft();
                }
                
                const data = await response.json();
                const sortedAgents = [...data].sort((a, b) => a.name.localeCompare(b.name)); // Ensure consistent order
                setAgents(sortedAgents);
                
                if (sortedAgents.length > 0 && !selectedAgent) {
                    setSelectedAgent(sortedAgents[0].id); // Set first agent only once
                    setSelectedAgentName(sortedAgents[0].name); // Set name as well
                }
            } catch (error) {
                console.error('Error fetching agents:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchAgents();
    }, [loginWithMicrosoft]);

    // Update the selected agent's name whenever the agent changes
    useEffect(() => {
        const agent = agents.find((agent) => agent.id === selectedAgent);
        setSelectedAgentName(agent?.name);
    }, [selectedAgent, agents]);

    return (
        <AgentContext.Provider value={{ agents, selectedAgent, selectedAgentName, setSelectedAgent, loading }}>
            {children}
        </AgentContext.Provider>
    );
};
